import { Component, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { CreatePersonInput, PersonGridItemObject, ServiceTypeGridItemObject, UpdatePersonInput } from '@it2go/types';
import { UpsertPersonPageStore } from './upsert-person-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { AsyncPipe, Location } from '@angular/common';
import { isWoman, vokativ } from 'vokativ';
import { fb, routeParams, translate } from '../../../../shared/shared.global-service';
import { dateify } from '../../../../shared/helpers/date.helper';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, map } from 'rxjs/operators';
import { filter } from 'rxjs';
import config from '../../../../../config/config';
import { VzpValidateRcObject } from '@libs/types/src/services/object-type/vzp/vzp.validate-rc-object';
import { contentGlobalSuccess } from '../../../content.error';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { FilterBuilder } from '../../../helper/filter.builder';
import { changeSignal } from '../../../../../utils/form.utils';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import {
    PersonEditBasicInfoComponent,
} from '../../../components/person/person-edit-basic-info/person-edit-basic-info.component';
import { PersonAddressesComponent } from '../../../components/person/person-addresses/person-addresses.component';
import { PersonInsuranceComponent } from '../../../components/person/person-insurance/person-insurance.component';
import {
    CreatePersonPageWorkplaceTableComponent,
} from './create-person-page-workplace-table/create-person-page-workplace-table.component';
import {
    PersonPaymentDetailsComponent,
} from '../../../components/person/person-payment-details/person-payment-details.component';
import {
    PersonAdditionalInfoComponent,
} from '../../../components/person/person-additional-info/person-additional-info.component';
import {
    PersonContactInfoComponent,
} from '../../../components/person/person-contact-info/person-contact-info.component';
import { FolderNotesComponent } from '../../../components/person/person-notes/person-notes.component';
import {
    PersonRestrictionsComponent,
} from '../../../components/person/person-restrictions/person-restrictions.component';
import {
    PersonAuthorizationsComponent,
} from '../../../components/person/person-authorizations/person-authorizations.component';
import {
    PersonFoldersInOrganizationComponent,
} from '../../../components/person/person-folders-in-organization/person-folders-in-organization.component';
import { CardComponent } from '../../../../../components/kit/card/card.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import {
    getPersonsForCheckFrag,
} from '../../../components/person/create-person-dialog/create-person-dialog.query';

function vokativWrapper(isForeign: boolean, name: string | null, isSurname: boolean) {
    if (isForeign || !name) return name;
    const vocative = vokativ(name, null, isSurname);
    return vocative.charAt(0).toUpperCase() + vocative.slice(1);
}

@Component({
    selector: 'app-upsert-person-page',
    templateUrl: 'upsert-person-page.component.html',
    styleUrls: ['upsert-person-page.component.scss'],
    providers: [UpsertPersonPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        AsyncPipe,
        PersonEditBasicInfoComponent,
        PersonAddressesComponent,
        PersonInsuranceComponent,
        CreatePersonPageWorkplaceTableComponent,
        PersonPaymentDetailsComponent,
        PersonAdditionalInfoComponent,
        PersonContactInfoComponent,
        FolderNotesComponent,
        PersonRestrictionsComponent,
        PersonAuthorizationsComponent,
        PersonFoldersInOrganizationComponent,
        CardComponent,
        DateInputComponent,
        ButtonComponent,
        FormatPersonPipe,
        TranslatePipe,
    ],
})
export class UpsertPersonPageComponent extends SubscriptionComponent {

    protected readonly store = inject(UpsertPersonPageStore);
    private readonly location = inject(Location);
    private readonly route = inject(ActivatedRoute);

    /** Režim "přidat další osobu do existující složky" - viz routa `add-person` v content.routes.ts. */
    protected readonly folderPersonCreate = this.route.snapshot.data['folderPersonCreate'] === true;

    /**
     * Editovaná osoba - další osoby složky chodí přes `edit/:personId`, bez něj se edituje
     * vlastník složky. Routa je společná pro celou složku, jinak by formulář vždy skončil u vlastníka.
     */
    protected readonly requestedPersonId = toSignal(
        this.route.paramMap.pipe(map((params) => Number(params.get('personId')) || null)),
        { initialValue: Number(this.route.snapshot.paramMap.get('personId')) || null },
    );

    /** Údaje složky (platby) se editují jen z karty jejího vlastníka. */
    protected readonly editsFolderOwner = computed(() => {
        const personId = this.requestedPersonId();
        const ownerId = this.store.folder()?.person?.id;

        return !personId || !ownerId || personId === ownerId;
    });

    protected form = fb.group({
        // For create:
        serviceId: fb.control<number | null>(null, Validators.required),
        image: fb.control<string | null>(null),

        // Basic Info
        titleBeforeName: fb.control<string | null>(null),
        titleAfterName: fb.control<string | null>(null),
        name: fb.control<string | null>(null),
        surname: fb.control<string | null>(null, Validators.required),
        documentName: fb.control<string | null>(null),
        documentSurname: fb.control<string | null>(null),
        alias: fb.control<string | null>(null),
        birthSurname: fb.control<string | null>(null),
        sex: fb.control<string>('male'),
        insuranceNumber: fb.control<string | null>(null),
        foreigner: fb.control<boolean>(false),
        dietSpec: fb.control<string>(''),

        // Contact Info
        phone: fb.control<string | null>(null),
        mobile: fb.control<string | null>(null),
        communikePassword: fb.control<string | null>('******'), // secret until viewed
        email: fb.control<string | null>(null, Validators.email),

        // Additional Info
        birthdate: fb.control<Date | null>(null),
        estimatedBirthYear: fb.control<number | null>(null),
        nationality: fb.control<string | null>('CZ'),
        supportMeasures: fb.control<string | null>(null),
        informationProviderSelect: fb.control<string | null>(null),
        informationProviderOther: fb.control<string | null>(null),
        dataVerification: fb.control<string | null>(null),
        verificationNumber: fb.control<string | null>(null),
        personNumber: fb.control<number | null>(null),
        dateDeath: fb.control<Date | null>(null),
    });

    // Payment Info - belongs to folder, not person
    protected paymentForm = fb.group({
        accountPrefix: fb.control<string | null>(null),
        accountNumber: fb.control<string | null>(null),
        accountBankCode: fb.control<string | null>(null),
        variableSymbol: fb.control<string | null>(null),
        specificSymbol: fb.control<string | null>(null),
        constantSymbol: fb.control<string | null>(null),
        messageForRecipient: fb.control<string | null>(null),
    });

    // Pobyt ve složce - patří k folder_person, ne k osobě, proto samostatná grupa
    // (save() spreaduje form.getRawValue() přímo do Create/UpdatePersonInput).
    protected folderPersonForm = fb.group({
        dateFrom: fb.control<Date | null>(new Date(), Validators.required),
    });

    /** Osoba nalezená podle RČ (resp. jména), aby v režimu folderPersonCreate nevznikla duplicita. */
    protected foundPerson = signal<PersonGridItemObject | null>(null);

    protected folderId: null | number = null;
    protected personId: null | number = null;
    protected person: any = null;

    protected addresses: any[] = [];
    protected insurances: any[] = [];
    protected restrictions: any[] = [];
    protected authorizations: any[] = [];
    protected note: string | null = null;

    protected estimatedBirthYearChanged = changeSignal(this.form.controls.estimatedBirthYear);
    protected insuranceNumberChanged = toSignal(this.form.controls.insuranceNumber.valueChanges
        .pipe(
            debounceTime(config.debounceTime),
            filter(x => (x?.length || 0) > 6),
        ));
    protected personNumberChanged = toSignal(this.form.controls.personNumber.valueChanges
        .pipe(
            debounceTime(config.debounceTime),
            filter(x => !!x),
        ));
    protected formChanged = toSignal(this.form.valueChanges);
    protected errorMsg = signal<string | null>(null);


    protected serviceIdChanged = changeSignal(this.form.controls.serviceId);
    protected serviceType = signal<ServiceTypeGridItemObject | null>(null);
    public isSocial = computed(() => {
        return this.serviceType()?.type === ServiceTypeEnum.SOCIAL;
    });
    public isHealthCare = computed(() => {
        return this.serviceType()?.type === ServiceTypeEnum.HEALTH_CARE;
    });

    constructor() {
        super();

        effect(() => {
            this.folderId = Number(routeParams.folderId());
            if (this.folderPersonCreate) {
                // osobu složky schválně nenačítáme - formulář by se přepnul do editace hlavní osoby
                this.store.getFolderContext({ id: this.folderId });
                return;
            }
            // složku potřebujeme i u další osoby - drží platby, pracoviště a typ služby
            this.store.getFolderPerson({ id: this.folderId });

            const personId = this.requestedPersonId();
            if (personId) {
                untracked(() => this.loadRequestedPerson(personId));
            }
        });

        effect(() => {
            const val = this.formChanged();
            if (val) {
                this.person = { ...this.person, ...{ name: val.name, surname: val.surname, sex: val.sex } };
            }
        });

        effect(() => {
            const insuranceNr = this.insuranceNumberChanged();

            if (insuranceNr && insuranceNr !== this.person?.insuranceNumber) {
                this.store.api.validateRc({ rc: insuranceNr }).then((value: VzpValidateRcObject) => {
                    if (value.birthDate) {
                        this.form.controls.birthdate.setValue(new Date(value.birthDate));
                    } else {
                        this.form.controls.birthdate.reset();
                    }
                    this.form.controls.sex.setValue(value.sex || this.form.value.sex || 'male');
                });
            }

            if (this.estimatedBirthYearChanged()) {
                this.form.controls.birthdate.clearValidators();
            } else {
                this.form.controls.birthdate.setValidators(Validators.required);
            }
            this.form.controls.birthdate.updateValueAndValidity();
        });

        effect(async () => {
            const serviceId = this.store.currentServiceId() || this.serviceIdChanged();
            if (serviceId) {
                const service = this.store.globalGlobalStore.organizationStore.getServiceById(serviceId)();
                if (service) {
                    const serviceTypeRes = await this.store.api.getServiceTypes(FilterBuilder.filterById(service.serviceTypeId));
                    this.serviceType.set(serviceTypeRes?.items?.[0] || null);
                }
                if (this.isSocial()) {
                    this.form.get('surname')?.clearValidators();
                    this.form.get('sex')?.addValidators(Validators.required);
                }
                if (this.isHealthCare()) {
                    this.form.get('surname')?.addValidators(Validators.required);
                    this.form.get('sex')?.clearValidators();
                }
                this.form.get('surname')?.updateValueAndValidity();
                this.form.get('sex')?.updateValueAndValidity();
            }
        });

        effect(async () => {
            const personNumber = this.personNumberChanged();
            if (!personNumber) return;

            const organizationId = this.store.globalGlobalStore.organizationStore.selectedId();
            if (!organizationId) return;

            const result = await this.store.api.getPersons(
                new FilterBuilder()
                    .where('organizationId', organizationId.toString())
                    .where('personNumber', personNumber.toString())
                    .limit(1)
                    .filter(),
                'id name surname',
            );

            const duplicate = result?.items?.[0];

            untracked(() => {
                if (!!duplicate && duplicate.id !== this.personId) {
                    this.form.controls.personNumber.setErrors({ notUnique: true });
                    this.errorMsg.set(translate.instant('person.msg.duplicatePersonNumber') + `: ${duplicate.name} ${duplicate.surname}`);
                } else {
                    this.form.controls.personNumber.updateValueAndValidity();
                    this.errorMsg.set(null);
                }
            });
        });

        if (history.state && history.state.name) {
            // Continue form from previous (simplyfied) page
            this.form.patchValue({
                name: history.state['name']?.trim(),
                surname: history.state['surname']?.trim(),
                insuranceNumber: history.state['insuranceNumber']?.trim(),
                serviceId: history.state['serviceId'],
                foreigner: Boolean(history.state['foreigner'] || false),
                birthdate: history.state['birthdate'],
                sex: history.state['foreigner']
                    ? 'male'
                    : isWoman(history.state['name']?.trim() || '')
                        ? 'female'
                        : 'male',
                documentName: vokativWrapper(history.state['foreigner'], history.state['name']?.trim() || '', false),
                documentSurname: vokativWrapper(history.state['foreigner'], history.state['surname']?.trim() || '', true),
            }, { emitEvent: true });
        }

        // Patch form on folder change
        effect(() => {
            const folder = this.store.folder();
            if (!folder) return;

            if (this.folderPersonCreate) {
                // serviceId je required a živí effect s typem služby - vezmeme ho ze složky
                untracked(() => this.form.patchValue({ serviceId: folder.serviceId }));
                this.form.controls.serviceId.disable();
                return;
            }

            this.form.controls.serviceId.disable();

            if (!folder.person) return;

            untracked(() => {
                this.paymentForm.patchValue({
                    ...folder.paymentDetails,
                });
            });

            // editovanou další osobu složky nesmí přepsat vlastník složky
            if (!this.editsFolderOwner()) return;

            this.personId = folder.person.id;
            this.person = folder.person;

            untracked(() => {
                this.form.patchValue({
                    ...folder.person,
                    birthdate: dateify(folder.person.birthdate),
                    dateDeath: dateify(folder.person.dateDeath),
                    serviceId: this.store.currentServiceId(),
                });
            });
        });

        // reload on deep edits
        this.store.doOnActions(
            () => {
                if (!this.folderId || this.folderPersonCreate) return;
                this.store.getFolderPerson({ id: this.folderId });

                const personId = this.requestedPersonId();
                if (personId) this.loadRequestedPerson(personId);
            },
            'unassignWorkplaceFromFolder',
            'updateFolderWorkplace',
            'updatePerson',
        );

        if (this.folderPersonCreate) {
            this.subs.push(
                this.form.controls.insuranceNumber.valueChanges.pipe(
                    debounceTime(config.debounceTime),
                ).subscribe((insuranceNumber) => {
                    if ((insuranceNumber?.length || 0) > 6) {
                        this.findExistingPerson({ insuranceNumber: insuranceNumber! });
                    }
                }),
                this.form.valueChanges.pipe(
                    debounceTime(config.debounceTime),
                ).subscribe((values) => {
                    if (this.isSocial() && values.surname) {
                        this.findExistingPerson({
                            surname: values.surname,
                            name: values.name,
                            birthdate: values.birthdate,
                        });
                    }
                }),
            );
        }
    }

    public cancel(): void {
        this.location.back();
    }

    /** Osoba z cesty - jiná než vlastník složky, takže si ji musíme natáhnout zvlášť. */
    private async loadRequestedPerson(personId: number): Promise<void> {
        const person = await this.store.getPerson(personId);
        if (!person) return;

        this.personId = person.id;
        this.person = person;

        this.form.patchValue({
            ...person,
            birthdate: dateify(person.birthdate),
            dateDeath: dateify(person.dateDeath),
            serviceId: this.store.currentServiceId(),
        });
    }

    /**
     * Dohledá osobu, která už v organizaci existuje, aby šla do složky přidat místo zakládání duplicity.
     * Čte návratovou hodnotu - signál `data.getPersons` sdílí s kontrolou unikátnosti osobního čísla.
     */
    private async findExistingPerson(by: {
        insuranceNumber?: string;
        surname?: string;
        name?: string | null;
        birthdate?: Date | null;
    }): Promise<void> {
        const organizationId = this.store.globalGlobalStore.organizationStore.selectedId();
        if (!organizationId) return;

        const builder = new FilterBuilder().where('organizationId', organizationId.toString());
        if (by.insuranceNumber) builder.where('insuranceNumber', by.insuranceNumber);
        if (by.surname) builder.where('surname', by.surname);
        if (by.name) builder.where('name', by.name);
        if (by.birthdate) builder.where('birthday', new Date(by.birthdate).toISOString());

        const result = await this.store.api.getPersons(builder.limit(1).filter(), getPersonsForCheckFrag);
        this.foundPerson.set(result?.items?.[0] || null);
    }

    /** Přidá nalezenou existující osobu do složky (místo zakládání nové). */
    protected async addFoundPersonToFolder(): Promise<void> {
        const person = this.foundPerson();
        if (!person || !this.folderId || this.folderPersonForm.isInvalid()) return;

        await this.store.api.createFolderPerson({
            folderId: this.folderId,
            personId: person.id,
            dateFrom: this.folderPersonForm.value.dateFrom!.toISOString(),
        });
        contentGlobalSuccess.next('folder.persons.messages.added');
        await this.store.goToFolderDashboard(this.folderId);
    }

    public async save(): Promise<void> {
        if (this.form.isInvalid()) return;
        if (this.folderPersonCreate && this.folderPersonForm.isInvalid()) return;
        // osoba z cesty se nenačetla - uložením by vznikla nová osoba místo její editace
        if (this.requestedPersonId() && this.personId !== this.requestedPersonId()) return;

        const basePersonData: any = {
            ...this.form.getRawValue(),
            image: this.form.value.image || '',
            serviceId: undefined, // not in create
            birthdate: this.form.value.birthdate?.toISOString() || null,
            dateDeath: this.form.value.dateDeath?.toISOString() || null,
            estimatedBirthYear: this.form.value.estimatedBirthYear ? Number(this.form.value.estimatedBirthYear) : null,
            communikePassword: this.form.controls.communikePassword.dirty ? this.form.value.communikePassword : undefined,
        };

        const basePaymentData: any = {
            ...this.paymentForm.getRawValue(),
        };

        this.store.serviceId = Number(this.form.getRawValue().serviceId || 0);

        if (this.folderPersonCreate) {
            // nová osoba jde rovnou do existující složky - vlastní složku schválně nedostane
            const folderPerson = await this.store.api.createFolderPersonWithNewPerson({
                ...basePersonData,
                serviceId: undefined,
                folderId: this.folderId,
                dateFrom: this.folderPersonForm.value.dateFrom!.toISOString(),
            } as any);

            const personId = folderPerson.personId || folderPerson.person?.id;

            // Rychlý ohek, protože komponenty neumí pracovat s Create Person
            await Promise.all([
                ...this.addresses.map((data) => this.store.api.createPersonAddress({ ...data, personId })),
                ...this.insurances.map((data) => this.store.api.createPersonInsurance({ ...data, personId })),
                ...this.restrictions.map((data) => this.store.api.createPersonContact({ ...data, personId })),
                ...this.authorizations.map((data) => this.store.api.createPersonContact({ ...data, personId })),
                // poznámku už uložil FolderNotesDialogComponent - folderId je tu reálné
            ]);

            contentGlobalSuccess.next('folder.persons.messages.added');
            await this.store.goToFolderDashboard(this.folderId!);
            return;
        }

        if (this.personId) {
            const data = basePersonData as UpdatePersonInput;
            data.personId = this.personId;
            data.image = undefined;
            await this.store.api.updatePerson(data);

            // další osoba složky nemá vlastní složku (ani platby), po uložení se vracíme do té sdílené
            if (!this.editsFolderOwner()) {
                contentGlobalSuccess.next('common.updated');
                await this.store.goToFolderDashboard(this.folderId!);
                return;
            }

            await this.store.api.updateFolderPaymentDetails({
                ...basePaymentData,
                folderId: this.folderId,
            }).then(() => {
                this.store.goToDetail(this.personId!);
                contentGlobalSuccess.next('common.updated');
            });
        } else {
            const data = basePersonData as CreatePersonInput;
            data.serviceId = this.store.serviceId;

            const person = await this.store.api.createPerson(data);
            const folder = await this.store.getFolderIdByPerson(person.id, this.store.serviceId);
            await this.store.api.updateFolderPaymentDetails({
                ...basePaymentData,
                folderId: folder.id,
            });

            // Rychlý ohek, protože komponenty neumí pracovat s Create Person
            await Promise.all([
                ...this.addresses.map(async (data) => {
                    data.personId = person.id;
                    await this.store.api.createPersonAddress(data);
                }),
                ...this.insurances.map(async (data) => {
                    data.personId = person.id;
                    await this.store.api.createPersonInsurance(data);
                }),
                ...this.restrictions.map(async (data) => {
                    data.personId = person.id;
                    await this.store.api.createPersonContact(data);
                }),
                ...this.authorizations.map(async (data) => {
                    data.personId = person.id;
                    await this.store.api.createPersonContact(data);
                }),
                this.note ? this.store.api.createFolderNote({
                    note: this.note,
                    folderId: folder.id,
                }) : () => {
                },
            ]).then(() => {
                this.store.goToDetail(person.id);
                contentGlobalSuccess.next('common.updated');
            });
        }
    }

}
